import { EVoucher } from "../models/evoucher";

export function htmlToWhatsappText(html: string): string {
  const doc = new DOMParser().parseFromString(html || "", "text/html");

  const walk = (node: Node): string => {
    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent || "").replace(/\s+/g, " ");
    }
    if (!(node instanceof HTMLElement)) return "";

    const tag = node.tagName.toLowerCase();
    const children = Array.from(node.childNodes).map(walk).join("");

    switch (tag) {
      case "br":
        return "\n";
      case "p":
        return children.trim() + "\n";
      case "strong":
      case "b":
        return children ? `*${children.trim()}*` : "";
      case "em":
      case "i":
        return children ? `_${children.trim()}_` : "";
      case "s":
      case "del":
      case "strike":
        return children ? `~${children.trim()}~` : "";
      case "code":
      case "pre":
        return children ? `\`\`\`${children}\`\`\`` : "";
      case "ul": {
        const lines = Array.from(node.querySelectorAll(":scope > li")).map(
          (li) => "• " + walk(li).trim(),
        );
        return lines.join("\n") + "\n";
      }
      case "ol": {
        const items = Array.from(node.querySelectorAll(":scope > li"));
        return (
          items.map((li, i) => `${i + 1}. ${walk(li).trim()}`).join("\n") + "\n"
        );
      }
      case "li":
        return children + "\n";
      case "a": {
        const href = (node.getAttribute("href") || "").trim();
        const text = children.trim();
        if (href && text && text !== href) return `${text} ${href}`;
        return href || text;
      }
      default:
        return children;
    }
  };
  const result = walk(doc.body)
    .replace(/\n{3,}/g, "\n\n")
    .trim();

  return result;
}
export function whatsappTextToHtml(text: string): string {
  if (!text) return "";

  let html: string = text;

  // 1️⃣ Normalize escaped newlines → real newlines
  html = html.replace(/\\n/g, "\n");

  // 2️⃣ Escape HTML
  html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;");

  // 3️⃣ Code block (multiline-safe)
  html = html.replace(/```([\s\S]*?)```/g, "<pre><code>$1</code></pre>");

  // 4️⃣ Text formatting
  html = html.replace(/\*(.*?)\*/g, "<b>$1</b>");
  html = html.replace(/_(.*?)_/g, "<i>$1</i>");
  html = html.replace(/~(.*?)~/g, "<s>$1</s>");

  // 5️⃣ Ordered list: 1. a \n 2. b
  html = html.replace(
    /(^|\n)(\d+\.\s.*(?:\n\d+\.\s.*)*)/g,
    (_: string, prefix: string, block: string): string => {
      const items: string = block
        .trim()
        .split("\n")
        .map((line: string) => line.replace(/^\d+\.\s*/, "").trim())
        .map((item: string) => `<li>${item}</li>`)
        .join("");

      return `${prefix}<ol>${items}</ol>`;
    },
  );

  // 6️⃣ Unordered list: - a \n - b
  html = html.replace(
    /(^|\n)([-•]\s.*(?:\n[-•]\s.*)*)/g,
    (_: string, prefix: string, block: string): string => {
      const items: string = block
        .trim()
        .split("\n")
        .map((line: string) => line.replace(/^[-•]\s*/, "").trim())
        .map((item: string) => `<li>${item}</li>`)
        .join("");

      return `${prefix}<ul>${items}</ul>`;
    },
  );

  // 7️⃣ URLs → clickable links
  html = html.replace(
    /(https?:\/\/(?!getz\.co(?:\/a)?\/UNSUB)[^\s]+)/g,
    `<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>`,
  );

  // 8️⃣ Newlines → <br /> (order matters)
  html = html.replace(/\n\n/g, "<br /><br />").replace(/\n/g, "<br />");

  return html.trim();
}

export const mapToEVouchers = (payload: any): EVoucher[] => {
  const list = Array.isArray(payload?.eVouchers)
    ? payload.eVouchers
    : Array.isArray(payload)
      ? payload
      : [];

  return list.map((x: any): EVoucher => {
    const outlets =
      Array.isArray(x.outlets) && x.outlets.length
        ? x.outlets
            .map((o: any) => o?.outletName)
            .filter(Boolean)
            .join(", ")
        : "";

    return {
      Id: String(x.id ?? ""),
      Name: String(x.title ?? ""),
      Outlet: outlets,
      Price: Number(x.price ?? 0),
      SKU: String(x.sku ?? ""),
      StartDate: x?.validityPeriod?.startDateRange
        ? new Date(x.validityPeriod.startDateRange)
        : null,
      EndDate: x?.validityPeriod?.endDateRange
        ? new Date(x.validityPeriod.endDateRange)
        : null,
      ModifiedDate: x?.ModifiedDate ? new Date(x.ModifiedDate) : null,
      Status: "Active",
    };
  });
};
